import { Component, ElementRef, Inject, ViewChild, inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { PopupModule, PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { SVGIcon, plusIcon, xIcon } from '@progress/kendo-svg-icons';
import { AdminAppbarComponent } from '../layout/components/admin-appbar/admin-appbar.component';
import { ManagerFormComponent } from './components/manager-form/manager-form.component';
import { BaseComponent } from '../../../core/shared/common/base.component';
import { DatatableComponent } from '../../../core/components/datatable/datatable.component';
import { DetectionManager } from '../../../core/models/entities';
import { environment } from '../../../../environments/environment';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-manager',
  standalone: true,
  imports: [CommonModule, AdminAppbarComponent, ButtonModule, PopupModule, DropDownListModule, DatatableComponent],
  templateUrl: './manager.page.html',
  styleUrl: './manager.page.scss',
})
export class ManagerPage extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  private popupService = inject(PopupService);
  public plusIcon: SVGIcon = plusIcon;
  public xIcon: SVGIcon = xIcon;
  show: boolean | undefined;
  private popupRef: PopupRef | undefined;
  bodyElement: ElementRef;

  public datatableUrl = `${environment.apiUrl}/v1/detection/administrative/detection/`;

  public statusOptions = [
    { text: 'Active', value: 'Active' },
    { text: 'Disabled', value: 'Disabled' },
  ];

  public defaultStateOptions = [
    { text: 'On', value: 'On' },
    { text: 'Muted', value: 'Muted' },
  ];

  private statusFilter: string | null = null;
  private defaultStateFilter: string | null = null;

  public initialFilters = this.buildFilters();

  public datatableColumns = [
    { data: 'title', name: 'title', title: 'Title', type: 'string', render: DataTable.render.text() },
    { data: 'description', name: 'description', title: 'Description', type: 'string', render: DataTable.render.text() },
    { data: 'status', name: 'status', title: 'Status', type: 'string', render: DataTable.render.text() },
    {
      data: 'default_state',
      name: 'default_state',
      title: 'Default State',
      type: 'string',
      render: DataTable.render.text(),
    },
  ];

  constructor(@Inject(DOCUMENT) private document: Document) {
    super();
    this.bodyElement = new ElementRef(this.document.body);
  }

  private buildFilters(): Record<string, string> {
    const filters: Record<string, string> = { $orderby: 'title' };
    const expressions: string[] = [];
    if (this.statusFilter != null) {
      expressions.push(`status eq '${this.statusFilter}'`);
    }
    if (this.defaultStateFilter != null) {
      expressions.push(`default_state eq '${this.defaultStateFilter}'`);
    }
    if (expressions.length) {
      filters['$filter'] = expressions.join(' and ');
    }
    return filters;
  }

  public statusChange($event: { text: string; value: string | null }): void {
    this.statusFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public defaultStateChange($event: { text: string; value: string | null }): void {
    this.defaultStateFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public addDetection(anchor: ElementRef | HTMLElement): void {
    this.togglePopup();
  }

  public editDetection(row: unknown): void {
    this.togglePopup(row as DetectionManager);
  }

  public togglePopup(detection = new DetectionManager()): void {
    if (this.popupRef) {
      this.popupRef.close();
      this.popupRef = undefined;
    } else {
      this.popupRef = this.popupService.open({
        anchor: this.bodyElement,
        content: ManagerFormComponent,
        animate: { type: 'slide', direction: 'left', duration: 200 },
        anchorAlign: { horizontal: 'right', vertical: 'top' },
        popupAlign: { horizontal: 'right', vertical: 'top' },
      });

      const managerComponent = this.popupRef.content.instance as ManagerFormComponent;

      if (detection) managerComponent.detection.set(detection);

      managerComponent.close.subscribe(() => {
        this.table?.reload();
        this.togglePopup();
      });
    }
  }
}
